import React, { useCallback, useEffect, useMemo, useState } from "react";

import { getControlsHealth } from "../../api/controls.api.js";
import Loader from "../common/Loader";
import Button from "../../ui/Button";
import Table from "../../ui/Table";
import StatusBadge from "../../ui/StatusBadge";
import ControlEffectivenessBadge from "./ControlEffectivenessBadge";
import { useControlEffectivenessConfig } from "../../hooks/useControlEffectivenessConfig";
import ControlsHealthPrintModal from "./print/ControlsHealthPrintModal";
import ControlsHealthPrintPreview from "./print/ControlsHealthPrintPreview";
import ControlsHealthPrintStyles from "./print/ControlsHealthPrintStyles";
import { colors, radius, shadows, spacing, typography } from "../../ui/tokens";
import {
  sectionHeaderStyle,
  surfaceCardBodyStyle,
  surfaceCardHeaderStyle,
  surfaceCardStyle,
  toolbarStyle,
} from "../../ui/pagePatterns";
import { trackUiEvent } from "../../utils/analytics";

const KPI_DEFINITIONS = [
  { key: "ineffective", label: "Ineffective", accessor: (data) => data?.ineffective_controls || 0, tone: colors.danger },
  { key: "overdue_review", label: "Overdue reviews", accessor: (data) => data?.overdue_reviews || 0, tone: colors.warning },
  { key: "overdue_test", label: "Overdue tests", accessor: (data) => data?.overdue_tests || 0, tone: "#7c3aed" },
  { key: "missing_owner", label: "Missing owner", accessor: (data) => data?.missing_owner || 0, tone: "#0ea5e9" },
  { key: "missing_evidence", label: "Missing evidence", accessor: (data) => data?.missing_evidence || 0, tone: "#f59e0b" },
  {
    key: "open_actions",
    label: "Controls with open actions",
    accessor: (data) => data?.controls_with_open_actions || 0,
    tone: colors.accent,
  },
];

const formatDate = (value) => {
  if (!value) return "Not set";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Not set";
  return date.toLocaleDateString();
};

const formatDueLabel = (row) => {
  if (row.next_test_due) return `Test due ${formatDate(row.next_test_due)}`;
  if (row.next_review_due) return `Review due ${formatDate(row.next_review_due)}`;
  return "No due date";
};

const formatRecentActivity = (row) => {
  if (row.last_test_date) return `Last tested ${formatDate(row.last_test_date)}`;
  if (row.last_review_date) return `Last reviewed ${formatDate(row.last_review_date)}`;
  return "No recent review/test";
};

const buildActiveFilterPills = (filters = {}) => {
  const pills = [];
  if (filters?.asOf) pills.push(`As at ${formatDate(filters.asOf)}`);
  if (filters?.search) pills.push(`Search: ${filters.search}`);
  if (filters?.status) pills.push(`Status: ${filters.status}`);
  if (filters?.owner) pills.push(`Owner: ${filters.owner}`);
  if (filters?.category) pills.push(`Category: ${filters.category}`);
  if (filters?.catalog) pills.push(`Catalog: ${filters.catalog}`);
  return pills;
};

const reasonChipStyle = {
  display: "inline-flex",
  alignItems: "center",
  borderRadius: 999,
  border: `1px solid ${colors.border}`,
  padding: "2px 8px",
  fontSize: typography.size.xs,
  background: colors.surfaceSubtle,
  color: colors.text,
};

export default function ControlsHealthPanel({
  active,
  filters,
  refreshNonce = 0,
  onRefresh,
  onOpenControl,
  onDrillDown,
  filterContent,
}) {
  const { items: effectivenessConfig } = useControlEffectivenessConfig();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [printModalOpen, setPrintModalOpen] = useState(false);
  const [printPreviewVisible, setPrintPreviewVisible] = useState(false);
  const searchFilter = filters?.search;
  const statusFilter = filters?.status;
  const ownerFilter = filters?.owner;
  const categoryFilter = filters?.category;
  const catalogFilter = filters?.catalog;
  const asOfFilter = filters?.asOf;

  useEffect(() => {
    if (!active) return;
    let cancelled = false;

    const loadHealth = async () => {
      setLoading(true);
      setError("");
      try {
        const response = await getControlsHealth({
          search: searchFilter,
          status: statusFilter,
          owner: ownerFilter,
          category: categoryFilter,
          catalog: catalogFilter,
          as_of: asOfFilter,
        });
        if (cancelled) return;
        const payload = response?.data ?? response;
        setData(payload);
      } catch (err) {
        if (cancelled) return;
        setError(err?.message || "Unable to load controls health.");
        setData(null);
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    loadHealth();
    return () => {
      cancelled = true;
    };
  }, [
    active,
    searchFilter,
    statusFilter,
    ownerFilter,
    categoryFilter,
    catalogFilter,
    asOfFilter,
    refreshNonce,
  ]);

  const kpis = useMemo(
    () =>
      KPI_DEFINITIONS.map((metric) => ({
        ...metric,
        value: metric.accessor(data),
      })),
    [data]
  );
  const activeFilterPills = useMemo(() => buildActiveFilterPills(filters), [filters]);
  const printOverflow = useMemo(
    () => ({
      likelyOverflow:
        (data?.attention_items?.length || 0) > 6
        || (data?.attention_by_owner?.length || 0) > 6
        || (data?.recent_changes?.length || 0) > 4,
    }),
    [data]
  );
  const handleOpenPrintModal = useCallback(() => {
    setPrintPreviewVisible(false);
    setPrintModalOpen(true);
  }, []);
  const handlePreviewPrint = useCallback(() => {
    setPrintPreviewVisible(true);
  }, []);
  const handlePrint = useCallback(() => {
    setPrintPreviewVisible(true);
    window.setTimeout(() => {
      if (typeof window.print === "function") {
        window.print();
      }
    }, 0);
  }, []);

  const attentionColumns = useMemo(
    () => [
      {
        id: "control",
        header: "Control",
        render: (row) => (
          <div style={{ display: "grid", gap: 4 }}>
            <button
              type="button"
              onClick={(event) => {
                event.stopPropagation();
                trackUiEvent("controls_health_attention_row_opened", { controlId: row.control_id, source: "name" });
                onOpenControl?.(row.control_id);
              }}
              style={linkButtonStyle}
            >
              {row.control_name}
            </button>
            <span style={{ fontSize: typography.size.xs, color: colors.textMuted }}>{row.control_identifier}</span>
          </div>
        ),
      },
      {
        id: "reason",
        header: "Reason",
        render: (row) => (
          <div style={{ display: "flex", flexWrap: "wrap", gap: spacing.xs }}>
            {(row.reasons || []).map((reason) => (
              <span key={reason} style={reasonChipStyle}>
                {reason}
              </span>
            ))}
          </div>
        ),
      },
      {
        id: "owner",
        header: "Owner",
        accessor: (row) => row.owner || "Unassigned",
      },
      {
        id: "status",
        header: "Status / Effectiveness",
        render: (row) => (
          <div style={{ display: "grid", gap: 4 }}>
            <StatusBadge variant="status" value={row.status} fallbackLabel={row.status || "Unknown"} />
            <ControlEffectivenessBadge value={row.effectiveness_rating} config={effectivenessConfig} />
          </div>
        ),
      },
      {
        id: "due",
        header: "Due",
        accessor: (row) => formatDueLabel(row),
      },
      {
        id: "recent",
        header: "Last review/test",
        accessor: (row) => formatRecentActivity(row),
      },
      {
        id: "open",
        header: "Open work",
        accessor: (row) => {
          const parts = [];
          if (row.open_actions_count) parts.push(`${row.open_actions_count} action${row.open_actions_count === 1 ? "" : "s"}`);
          if (row.open_findings_count) parts.push(`${row.open_findings_count} finding${row.open_findings_count === 1 ? "" : "s"}`);
          return parts.length ? parts.join(", ") : "None";
        },
      },
      {
        id: "action",
        header: "Action",
        render: (row) => (
          <Button
            type="button"
            variant="secondary"
            onClick={(event) => {
              event.stopPropagation();
              trackUiEvent("controls_health_attention_row_opened", { controlId: row.control_id, source: "button" });
              onOpenControl?.(row.control_id);
            }}
          >
            Open
          </Button>
        ),
      },
    ],
    [effectivenessConfig, onOpenControl]
  );

  return (
    <div style={{ display: "grid", gap: spacing.xl }}>
      <div style={surfaceCardStyle}>
        <div style={surfaceCardHeaderStyle}>
          <div style={sectionHeaderStyle}>
            <div>
              <div style={{ fontSize: typography.size.xs, color: colors.textMuted }}>Controls health</div>
              <div style={{ fontWeight: typography.weight.medium, fontSize: 16, color: colors.text }}>
                What needs attention right now
              </div>
            </div>
            <div style={{ fontSize: typography.size.xs, color: colors.textSubtle }}>
              Showing metrics for all controls matching current filters.
            </div>
          </div>
          <div style={toolbarStyle}>
            <Button
              type="button"
              variant="secondary"
              onClick={handleOpenPrintModal}
              disabled={loading || !data || Boolean(error)}
            >
              Print controls health to one page
            </Button>
            <Button type="button" variant="secondary" onClick={() => onRefresh && onRefresh()}>
              Refresh health
            </Button>
          </div>
        </div>

        {filterContent && <div style={healthFilterWrapStyle}>{filterContent}</div>}

        <div style={{ ...surfaceCardBodyStyle, display: "grid", gap: spacing.xl }}>
          {loading && <Loader label="Loading controls health..." />}
          {!loading && error && <div style={errorBannerStyle}>{error}</div>}

          {!loading && !error && data && (
            <>
              {activeFilterPills.length > 0 && (
                <div style={activeFilterWrapStyle}>
                  {activeFilterPills.map((pill) => (
                    <span key={pill} style={activeFilterChipStyle}>
                      {pill}
                    </span>
                  ))}
                </div>
              )}

              <div style={{ display: "grid", gap: spacing.xs }}>
                <div style={{ fontSize: typography.size.sm, color: colors.text }}>{data.scope_note}</div>
                {data.limitation_note && (
                  <div style={{ fontSize: typography.size.xs, color: colors.textMuted }}>{data.limitation_note}</div>
                )}
              </div>

              {data.total_controls === 0 && (
                <div style={emptyPanelStyle}>
                  <div style={{ fontWeight: typography.weight.medium }}>No controls match the current filters</div>
                  <div style={{ fontSize: typography.size.sm, color: colors.textSubtle }}>
                    Adjust the register filters to review a different slice of the controls library.
                  </div>
                </div>
              )}

              {data.total_controls > 0 && (
                <>
                  <div style={kpiGridStyle}>
                    {kpis.map((metric) => (
                      <button
                        key={metric.key}
                        type="button"
                        onClick={() => {
                          if (!metric.value) return;
                          trackUiEvent("controls_health_kpi_clicked", { metric: metric.key, value: metric.value });
                          onDrillDown?.({
                            type: "metric",
                            label: metric.label,
                            healthReason: metric.key,
                          });
                        }}
                        disabled={!metric.value}
                        style={{
                          ...kpiCardStyle,
                          cursor: metric.value ? "pointer" : "default",
                          opacity: metric.value ? 1 : 0.72,
                        }}
                      >
                        <div style={{ fontSize: typography.size.xs, color: colors.textMuted }}>{metric.label}</div>
                        <div style={{ fontSize: 28, fontWeight: typography.weight.bold, color: metric.tone }}>{metric.value}</div>
                        <div style={{ fontSize: typography.size.xs, color: colors.textSubtle }}>
                          {metric.value ? "Open matching controls in the register" : "No controls currently match"}
                        </div>
                      </button>
                    ))}
                  </div>

                  <div style={{ display: "grid", gap: spacing.md }}>
                    <div style={sectionHeaderStyle}>
                      <div>
                        <div style={{ fontWeight: typography.weight.medium, color: colors.text }}>Needs attention</div>
                        <div style={{ fontSize: typography.size.xs, color: colors.textMuted }}>
                          Prioritized by ineffective controls first, then overdue work, then missing ownership or evidence.
                        </div>
                      </div>
                      <div style={{ fontSize: typography.size.xs, color: colors.textSubtle }}>
                        {data.attention_controls} control{data.attention_controls === 1 ? "" : "s"} currently need attention
                      </div>
                    </div>

                    {Array.isArray(data.attention_items) && data.attention_items.length > 0 ? (
                      <Table
                        columns={attentionColumns}
                        data={data.attention_items}
                        getRowKey={(row) => row.control_id}
                        onRowClick={(row) => {
                          trackUiEvent("controls_health_attention_row_opened", { controlId: row.control_id, source: "row" });
                          onOpenControl?.(row.control_id);
                        }}
                        emptyState="No controls currently need attention."
                      />
                    ) : (
                      <div style={emptyPanelStyle}>
                        <div style={{ fontWeight: typography.weight.medium }}>No controls currently need attention</div>
                        <div style={{ fontSize: typography.size.sm, color: colors.textSubtle }}>
                          Current filters show no ineffective, overdue, ownerless, or evidence-light controls.
                        </div>
                      </div>
                    )}
                  </div>

                  <div style={breakdownShellStyle}>
                    <div style={sectionHeaderStyle}>
                      <div>
                        <div style={{ fontWeight: typography.weight.medium, color: colors.text }}>Attention by owner</div>
                        <div style={{ fontSize: typography.size.xs, color: colors.textMuted }}>
                          Click an owner to open their controls that currently need attention.
                        </div>
                      </div>
                    </div>
                    {Array.isArray(data.attention_by_owner) && data.attention_by_owner.length > 0 ? (
                      <div style={{ display: "grid", gap: spacing.sm }}>
                        {data.attention_by_owner.map((bucket) => {
                          const share =
                            data.attention_controls > 0 ? Math.round((bucket.count / data.attention_controls) * 100) : 0;
                          return (
                            <button
                              key={bucket.owner}
                              type="button"
                              onClick={() => {
                                trackUiEvent("controls_health_chart_segment_clicked", {
                                  segment: "attention_by_owner",
                                  owner: bucket.owner,
                                  count: bucket.count,
                                });
                                onDrillDown?.({
                                  type: "owner",
                                  label:
                                    bucket.owner === "Unassigned"
                                      ? "Missing owner"
                                      : `${bucket.owner} attention controls`,
                                  healthReason: bucket.owner === "Unassigned" ? "missing_owner" : "needs_attention",
                                  owner: bucket.owner === "Unassigned" ? null : bucket.owner,
                                });
                              }}
                              style={ownerBreakdownButtonStyle}
                            >
                              <div style={ownerBreakdownLabelStyle}>
                                <span>{bucket.owner}</span>
                                <span style={{ color: colors.textMuted }}>
                                  {bucket.count} ({share}%)
                                </span>
                              </div>
                              <div style={ownerTrackStyle}>
                                <div style={{ ...ownerFillStyle, width: `${share}%` }} />
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    ) : (
                      <div style={{ fontSize: typography.size.sm, color: colors.textSubtle }}>
                        No owner breakdown is available because no controls currently need attention.
                      </div>
                    )}
                  </div>

                  {Array.isArray(data.recent_changes) && data.recent_changes.length > 0 && (
                    <div style={secondarySectionStyle}>
                      <div style={sectionHeaderStyle}>
                        <div>
                          <div style={{ fontWeight: typography.weight.medium, color: colors.text }}>Recently changed controls</div>
                          <div style={{ fontSize: typography.size.xs, color: colors.textMuted }}>
                            Secondary context. Use this to decide what to inspect next.
                          </div>
                        </div>
                      </div>
                      <div style={{ display: "grid", gap: spacing.sm }}>
                        {data.recent_changes.map((item) => (
                          <button
                            key={item.control_id}
                            type="button"
                            onClick={() => onOpenControl?.(item.control_id)}
                            style={recentChangeButtonStyle}
                          >
                            <div style={{ display: "grid", gap: 4, textAlign: "left" }}>
                              <span style={{ fontWeight: typography.weight.medium, color: colors.text }}>{item.control_name}</span>
                              <span style={{ fontSize: typography.size.xs, color: colors.textMuted }}>
                                {item.control_identifier} | {item.owner || "Unassigned"} | Updated {formatDate(item.updated_at)}
                              </span>
                            </div>
                            <StatusBadge
                              variant="status"
                              value={item.effectiveness_rating || item.status}
                              fallbackLabel={item.effectiveness_rating || item.status || "Unknown"}
                            />
                            {item.effectiveness_rating && (
                              <ControlEffectivenessBadge value={item.effectiveness_rating} config={effectivenessConfig} />
                            )}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              )}
            </>
          )}
        </div>
      </div>
      {printModalOpen && <ControlsHealthPrintStyles />}
      <ControlsHealthPrintModal
        open={printModalOpen}
        data={data}
        filters={filters}
        previewVisible={printPreviewVisible}
        overflow={printOverflow}
        onCancel={() => {
          setPrintModalOpen(false);
          setPrintPreviewVisible(false);
        }}
        onPreview={handlePreviewPrint}
        onPrint={handlePrint}
      />
      {printModalOpen && data && (
        <ControlsHealthPrintPreview
          data={data}
          filters={filters}
          root
          testId="controls-health-print-preview-root"
        />
      )}
    </div>
  );
}

const healthFilterWrapStyle = {
  ...surfaceCardBodyStyle,
  borderTop: `1px solid ${colors.border}`,
  borderBottom: `1px solid ${colors.border}`,
};

const activeFilterWrapStyle = {
  display: "flex",
  flexWrap: "wrap",
  gap: spacing.xs,
};

const activeFilterChipStyle = {
  display: "inline-flex",
  alignItems: "center",
  borderRadius: 999,
  border: `1px solid ${colors.border}`,
  padding: "4px 10px",
  fontSize: typography.size.xs,
  background: colors.surfaceSubtle,
  color: colors.text,
};

const kpiGridStyle = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
  gap: spacing.md,
};

const kpiCardStyle = {
  border: `1px solid ${colors.border}`,
  borderRadius: radius.lg,
  background: colors.surface,
  boxShadow: shadows.xs,
  padding: spacing.xl,
  display: "grid",
  gap: spacing.xs,
  textAlign: "left",
};

const breakdownShellStyle = {
  border: `1px solid ${colors.border}`,
  borderRadius: radius.lg,
  background: colors.surface,
  boxShadow: shadows.xs,
  padding: spacing.xl,
  display: "grid",
  gap: spacing.md,
};

const ownerBreakdownButtonStyle = {
  border: "none",
  background: "transparent",
  padding: 0,
  display: "grid",
  gap: spacing.xs,
  textAlign: "left",
  cursor: "pointer",
};

const ownerBreakdownLabelStyle = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: spacing.sm,
  color: colors.text,
  fontSize: typography.size.sm,
};

const ownerTrackStyle = {
  width: "100%",
  height: 10,
  borderRadius: 999,
  background: colors.surfaceMuted,
  overflow: "hidden",
};

const ownerFillStyle = {
  height: "100%",
  borderRadius: 999,
  background: colors.accent,
};

const secondarySectionStyle = {
  borderTop: `1px solid ${colors.border}`,
  paddingTop: spacing.xl,
  display: "grid",
  gap: spacing.md,
};

const recentChangeButtonStyle = {
  border: `1px solid ${colors.border}`,
  borderRadius: radius.md,
  background: colors.surface,
  padding: spacing.md,
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: spacing.md,
  cursor: "pointer",
};

const emptyPanelStyle = {
  border: `1px solid ${colors.border}`,
  borderRadius: radius.lg,
  background: colors.surfaceSubtle,
  padding: spacing.xl,
  display: "grid",
  gap: spacing.xs,
};

const errorBannerStyle = {
  border: `1px solid ${colors.dangerBorder}`,
  background: colors.dangerSubtle,
  color: colors.danger,
  borderRadius: radius.md,
  padding: `${spacing.sm}px ${spacing.md}px`,
  fontSize: typography.size.xs,
};

const linkButtonStyle = {
  border: "none",
  background: "transparent",
  color: colors.accent,
  padding: 0,
  margin: 0,
  textAlign: "left",
  cursor: "pointer",
  fontSize: typography.size.sm,
  fontWeight: typography.weight.medium,
};
